// Fix CM (2026-09-13): panel único de "Tareas y Flujo de Trabajo". Es el mismo componente
// en todos los módulos: se le pasa el módulo y, si corresponde, el documento del contexto
// (obra, pedido, presupuesto). Muestra las tareas del sistema (engranaje, azules) y las
// manuales (persona, verdes), con tres vistas: pendientes en columnas, línea de tiempo y
// filtros rápidos. "No se busca un módulo de tareas aislado, sino un sistema transversal
// de flujo de trabajo que se incruste en cada sección del ERP" (CEN, 13/09).
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Cog, User as UserIcon, Plus, Clock, Loader2, CheckCircle2, AlertTriangle, Trash2,
  MessageSquare, ArrowRight, X, Lock, Pencil, Undo2,
} from "lucide-react";
import { API, useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { formatDate } from "../utils/format";

const opts = { withCredentials: true };
const ESTADOS = [["pendiente", "Por hacer"], ["en_proceso", "En proceso"], ["hecha", "Hecho"]];
const PRIORIDADES = [["normal", "Normal"], ["alta", "Alta"], ["urgente", "Urgente"]];
const COLOR_PRIORIDAD = { normal: "bg-gray-100 text-gray-600", alta: "bg-amber-100 text-amber-700", urgente: "bg-red-100 text-red-700" };
const ROLES_ASIGNABLES = ["Jefe de Compras", "Jefe de Logística", "Gerencia Técnica", "Gerencia Financiera",
  "Coordinador de Obra", "Residente de Obra", "Ingeniería", "Proyectista", "Contador", "Jefe de RRHH", "Administrador"];
// Fix CV (2026-09-14): "se están generando las tareas en categorías aleatorias… hay que
// habilitar la opción de elegir a cuál módulo corresponde la tarea creada de forma
// manual" (Heber, 14/09). Antes el módulo salía de la pestaña en la que estabas: desde
// "Todos los módulos" no había ninguna, y todo caía en General. Son las mismas
// pestañas que se ven arriba; "Gabinete" se guarda como `ingenieria` y aparece bajo
// Gabinete, que agrupa Ingeniería y Presupuestos.
const MODULOS_ELEGIBLES = [
  ["compras", "Compras"], ["gabinete", "Gabinete"], ["obras", "Obras"], ["tesoreria", "Tesorería"],
  ["inventario", "Inventario"], ["rrhh", "RRHH"], ["finanzas", "Finanzas"],
  ["comercial", "Comercial"], ["general", "General"],
];
const MODULO_INICIAL = (m) => (m === "ingenieria" || m === "presupuestos" || m === "gabinete"
  ? "gabinete"
  : (MODULOS_ELEGIBLES.some(([v]) => v === m) ? m : "general"));

// Fix CR (2026-09-14): "habilitar un editor para las tareas porfavor, si hay error de
// tipeo o cambios en la tarea ahora mismo no da la opción de edición" (Heber, 14/09).
// Es el mismo modal: sin `tarea` crea, con `tarea` edita.
function NuevaTareaModal({ contexto, tarea, onClose, onSaved }) {
  const { user } = useAuth();
  const editando = !!tarea;
  const [f, setF] = useState({
    titulo: tarea?.titulo || "", descripcion: tarea?.descripcion || "",
    prioridad: tarea?.prioridad || "normal", fecha_limite: tarea?.fecha_limite || "",
    asignado_rol: tarea?.asignado_rol || "", asignado_user_id: tarea?.asignado_user_id || "",
    asignado_nombre: tarea?.asignado_nombre || "",
    // Fix CV (2026-09-14): al editar manda el módulo de la tarea; al crear, el de la
    // pantalla en la que se está (obra, compras, presupuesto). Si no hay ninguno,
    // arranca en General pero se puede cambiar antes de guardar.
    modulo: MODULO_INICIAL(tarea?.modulo || contexto?.modulo || "general"),
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));
  // Fix CZ (2026-09-14): las personas del desplegable salen de los usuarios activos del
  // ERP, no de una lista escrita a mano: un usuario nuevo aparece solo.
  const [personas, setPersonas] = useState([]);
  useEffect(() => {
    axios.get(`${API}/tareas/asignables`, opts)
      .then(({ data }) => setPersonas(data?.personas || []))
      .catch(() => setPersonas([]));   // sin padrón quedan los roles, como antes
  }, []);

  const guardar = async () => {
    if (!f.titulo.trim()) { toast.error("Escribí el título de la tarea"); return; }
    setSaving(true);
    try {
      // Fix CV (2026-09-14): antes el contexto pisaba lo que la persona elegía y el
      // módulo terminaba siendo el de la pestaña. Ahora el contexto aporta el
      // documento vinculado y el formulario manda en el resto, el módulo incluido.
      const cuerpo = { ...contexto, ...f };
      if (editando) {
        await axios.put(`${API}/tareas/${tarea._id}`, cuerpo, opts);
        toast.success("Tarea actualizada");
      } else {
        await axios.post(`${API}/tareas`, cuerpo, opts);
        toast.success("Tarea creada");
      }
      onSaved?.();
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al guardar la tarea"); }
    finally { setSaving(false); }
  };

  const paraMi = () => setF(prev => (prev.asignado_user_id
    ? { ...prev, asignado_user_id: "", asignado_nombre: "" }
    : { ...prev, asignado_user_id: String(user?._id || ""), asignado_nombre: user?.name || "", asignado_rol: "" }));

  // Fix CZ (2026-09-14): un solo desplegable para las dos cosas. El valor lleva prefijo
  // ("rol:" o "user:") porque dos personas pueden llamarse igual que un rol.
  const valorAsignado = f.asignado_user_id ? `user:${f.asignado_user_id}`
    : f.asignado_rol ? `rol:${f.asignado_rol}` : "";
  const elegirAsignado = (v) => {
    if (v.startsWith("user:")) {
      const id = v.slice(5);
      const p = personas.find(x => String(x._id) === id);
      // Se limpia el rol: si quedaran los dos, la tarea de Bryan le aparecería también a
      // todos los que comparten su rol, que es justo lo contrario de asignarla a alguien.
      setF(prev => ({ ...prev, asignado_user_id: id, asignado_nombre: p?.name || prev.asignado_nombre || "", asignado_rol: "" }));
    } else if (v.startsWith("rol:")) {
      setF(prev => ({ ...prev, asignado_rol: v.slice(4), asignado_user_id: "", asignado_nombre: "" }));
    } else {
      setF(prev => ({ ...prev, asignado_rol: "", asignado_user_id: "", asignado_nombre: "" }));
    }
  };
  // Las personas van agrupadas por su rol, en el orden en que llegan del padrón (ya viene
  // ordenado por rol y por nombre). Varias personas con el mismo rol caen todas bajo él.
  const personasPorRol = personas.reduce((acc, p) => {
    const ult = acc[acc.length - 1];
    if (ult && ult[0] === p.role) ult[1].push(p); else acc.push([p.role, [p]]);
    return acc;
  }, []);

  return (
    <div className="fixed inset-0 z-[9995] flex items-center justify-center p-4" data-testid="nueva-tarea-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <p className="font-bold text-gray-900">{editando ? "Editar tarea" : "Nueva tarea"}</p>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3">
          {contexto?.entidad_label && (
            <p className="text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
              Queda vinculada a <strong className="text-gray-700">{contexto.entidad_label}</strong>
            </p>
          )}
          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">Título</label>
            <input value={f.titulo} onChange={e => set("titulo", e.target.value)} autoFocus
              placeholder="Ej.: Llamar al proveedor por el retraso del transformador"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" data-testid="tarea-titulo" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">Descripción (opcional)</label>
            <textarea value={f.descripcion} onChange={e => set("descripcion", e.target.value)} rows={2}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" data-testid="tarea-descripcion" />
          </div>
          {/* Fix CV (2026-09-14): a qué módulo corresponde la tarea. */}
          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">Módulo</label>
            <select value={f.modulo} onChange={e => set("modulo", e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" data-testid="tarea-modulo">
              {MODULOS_ELEGIBLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <p className="text-[11px] text-gray-400 mt-1">Es la pestaña donde va a aparecer. Gabinete junta Ingeniería y Presupuestos.</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-500 block mb-1">Vence</label>
              <input type="date" value={f.fecha_limite} onChange={e => set("fecha_limite", e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" data-testid="tarea-fecha" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 block mb-1">Prioridad</label>
              <select value={f.prioridad} onChange={e => set("prioridad", e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" data-testid="tarea-prioridad">
                {PRIORIDADES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">Asignar a</label>
            <div className="flex gap-2">
              {/* Fix CZ (2026-09-14): el desplegable ofrece los roles Y las personas.
                  Asignar a un ROL es "que lo tome quien tenga ese rol"; asignar a una
                  PERSONA es de esa persona y de nadie más, así que los dos valores son
                  excluyentes: elegir uno limpia el otro. */}
              <select value={valorAsignado} onChange={e => elegirAsignado(e.target.value)}
                className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm" data-testid="tarea-rol">
                <option value="">Sin asignar</option>
                <optgroup label="Por rol — lo toma quien tenga ese rol">
                  {ROLES_ASIGNABLES.map(r => <option key={r} value={`rol:${r}`}>{r}</option>)}
                </optgroup>
                {personasPorRol.map(([rol, gente]) => (
                  <optgroup key={rol} label={rol || "Sin rol"}>
                    {gente.map(p => <option key={p._id} value={`user:${p._id}`}>{p.name}</option>)}
                  </optgroup>
                ))}
                {/* La persona que ya tenía la tarea, si hoy no está en el padrón (se dio
                    de baja): así al editar no se pierde ni se cambia sin querer. */}
                {f.asignado_user_id && !personas.some(p => String(p._id) === String(f.asignado_user_id)) && (
                  <option value={`user:${f.asignado_user_id}`}>{f.asignado_nombre || "Asignado actual"}</option>
                )}
              </select>
              <button type="button" onClick={paraMi}
                className={`px-3 py-2 rounded-lg text-xs font-semibold border ${f.asignado_user_id ? "bg-[#2E6DA4] text-white border-[#2E6DA4]" : "border-gray-200 text-gray-600"}`}
                data-testid="tarea-para-mi">Para mí</button>
            </div>
          </div>
          <div className="flex gap-3 pt-1">
            <button onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 rounded-xl py-2.5 text-sm font-medium hover:bg-gray-50">Cancelar</button>
            <button onClick={guardar} disabled={saving} className="flex-1 bg-[#2E6DA4] text-white rounded-xl py-2.5 text-sm font-semibold disabled:opacity-60" data-testid="tarea-guardar-btn">
              {saving ? "Guardando..." : editando ? "Guardar cambios" : "Crear tarea"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function TareaCard({ tarea, onCambiar, onBorrar, onComentar, onEditar }) {
  const [comentando, setComentando] = useState(false);
  const [texto, setTexto] = useState("");
  const navigate = useNavigate();
  const esSistema = tarea.origen === "sistema";
  const Icono = esSistema ? Cog : UserIcon;
  return (
    <div className={`rounded-xl border p-3 bg-white ${tarea.cuello_botella && tarea.estado !== "hecha" ? "border-red-300 ring-1 ring-red-100" : esSistema ? "border-[#2E6DA4]/30" : "border-emerald-200"}`}
      data-testid={`tarea-${tarea._id}`}>
      <div className="flex items-start gap-2">
        <span className={`mt-0.5 p-1 rounded-lg ${esSistema ? "bg-[#EBF3FB] text-[#2E6DA4]" : "bg-emerald-50 text-emerald-600"}`}>
          <Icono size={13} />
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900">{tarea.titulo}</p>
          {tarea.descripcion && <p className="text-xs text-gray-500 mt-0.5">{tarea.descripcion}</p>}
          <div className="flex flex-wrap items-center gap-1.5 mt-1.5 text-[11px]">
            {tarea.entidad_label && <span className="font-mono bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{tarea.entidad_label}</span>}
            {tarea.asignado_nombre
              ? <span className="text-gray-500">para {tarea.asignado_nombre}</span>
              : tarea.asignado_rol ? <span className="text-gray-500">rol: {tarea.asignado_rol}</span> : null}
            {tarea.prioridad !== "normal" && <span className={`px-1.5 py-0.5 rounded font-bold ${COLOR_PRIORIDAD[tarea.prioridad]}`}>{tarea.prioridad}</span>}
            {tarea.bloqueante && <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-red-50 text-red-600 font-bold"><Lock size={9} /> bloquea</span>}
            {tarea.fecha_limite && <span className={tarea.vencida ? "text-red-600 font-bold" : "text-gray-500"}>vence {formatDate(tarea.fecha_limite)}</span>}
            {tarea.cuello_botella && tarea.estado !== "hecha" && (
              <span className="flex items-center gap-0.5 text-red-600 font-bold"><AlertTriangle size={10} /> frenada {Math.round(tarea.horas_abierta)} h</span>
            )}
          </div>
          {(tarea.comentarios || []).length > 0 && (
            <div className="mt-2 space-y-1 border-l-2 border-gray-100 pl-2">
              {(tarea.comentarios || []).slice(-3).map((c, i) => (
                <p key={i} className="text-[11px] text-gray-500"><strong className="text-gray-700">{c.user_name}:</strong> {c.texto}</p>
              ))}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-1.5 mt-2">
            {tarea.estado !== "hecha" && (
              <button onClick={() => onCambiar(tarea, tarea.estado === "pendiente" ? "en_proceso" : "hecha")}
                className="px-2 py-1 rounded-lg bg-[#2E6DA4]/10 text-[#2E6DA4] text-[11px] font-semibold hover:bg-[#2E6DA4]/20"
                data-testid={`tarea-avanzar-${tarea._id}`}>
                {tarea.estado === "pendiente" ? "Tomar" : "Marcar hecha"}
              </button>
            )}
            {tarea.estado === "hecha" && (
              <span className="flex items-center gap-1 text-[11px] text-emerald-600 font-semibold"><CheckCircle2 size={11} /> {tarea.completada_por || "Hecha"}</span>
            )}
            {/* Fix CR (2026-09-14): "permitir retroceder en los pasos, actualmente una
                vez que se toma una tarea por terminada no se puede devolver al paso
                inicial" (Heber, 14/09). Un paso atrás por vez. Los registros de algo
                que ya pasó (un pago, un cobro) no se reabren. */}
            {tarea.estado !== "pendiente" && !tarea.es_registro && (
              <button onClick={() => onCambiar(tarea, tarea.estado === "hecha" ? "en_proceso" : "pendiente")}
                className="flex items-center gap-1 px-2 py-1 rounded-lg text-gray-500 text-[11px] font-semibold hover:bg-gray-100"
                data-testid={`tarea-retroceder-${tarea._id}`}>
                <Undo2 size={11} /> {tarea.estado === "hecha" ? "Reabrir" : "Volver a pendiente"}
              </button>
            )}
            {tarea.origen === "usuario" && (
              <button onClick={() => onEditar(tarea)}
                className="flex items-center gap-1 px-2 py-1 rounded-lg text-gray-500 text-[11px] font-semibold hover:bg-gray-100"
                data-testid={`tarea-editar-${tarea._id}`}>
                <Pencil size={11} /> Editar
              </button>
            )}
            {tarea.accion_url && (
              <button onClick={() => navigate(tarea.accion_url)} className="flex items-center gap-1 px-2 py-1 rounded-lg bg-gray-100 text-gray-700 text-[11px] font-semibold hover:bg-gray-200"
                data-testid={`tarea-ir-${tarea._id}`}>
                <ArrowRight size={11} /> Ir a resolverla
              </button>
            )}
            <button onClick={() => setComentando(c => !c)} className="flex items-center gap-1 px-2 py-1 rounded-lg text-gray-500 text-[11px] font-semibold hover:bg-gray-100"
              data-testid={`tarea-comentar-${tarea._id}`}><MessageSquare size={11} /> Comentar</button>
            {tarea.origen === "usuario" && (
              <button onClick={() => onBorrar(tarea)} className="text-red-400 hover:text-red-600 ml-auto" data-testid={`tarea-borrar-${tarea._id}`}><Trash2 size={12} /></button>
            )}
          </div>
          {comentando && (
            <div className="flex gap-2 mt-2">
              <input value={texto} onChange={e => setTexto(e.target.value)} placeholder="Escribí un comentario"
                className="flex-1 border border-gray-200 rounded-lg px-2 py-1 text-xs" data-testid={`tarea-comentario-input-${tarea._id}`} />
              <button onClick={() => { onComentar(tarea, texto); setTexto(""); setComentando(false); }}
                className="px-3 py-1 rounded-lg bg-[#2E6DA4] text-white text-xs font-semibold" data-testid={`tarea-comentario-enviar-${tarea._id}`}>Enviar</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function TareasPanel({ modulo = "general", entidadTipo = "", entidadId = "", entidadLabel = "", titulo = "Tareas y Flujo de Trabajo", rolAsignado = "" }) {
  const [tareas, setTareas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vista, setVista] = useState("pendientes");     // pendientes | timeline
  const [filtro, setFiltro] = useState("todas");        // todas | sistema | mias
  // Fix CU (2026-09-14): "en este apartado necesitamos un filtro de Persona asignada o
  // rol" (Edson, 14/09). Un solo desplegable con las dos cosas: arriba las personas con
  // nombre propio y abajo los roles, porque una tarea puede estar asignada a cualquiera
  // de los dos. Las opciones salen de las tareas que ya están en pantalla, así no hay
  // que traer el padrón de usuarios ni elegir a alguien que no tiene nada asignado.
  const [filtroAsignado, setFiltroAsignado] = useState("");   // "" | "persona:<id>" | "rol:<rol>" | "sin_asignar"
  const [nueva, setNueva] = useState(false);
  const [editando, setEditando] = useState(null);   // Fix CR (2026-09-14)

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (modulo) params.set("modulo", modulo);
      if (entidadTipo) params.set("entidad_tipo", entidadTipo);
      if (entidadId) params.set("entidad_id", entidadId);
      // Fix DO (2026-09-22): el panel del Cómputo/Proyecto solo trae las tareas del Proyectista.
      if (rolAsignado) params.set("asignado_rol", rolAsignado);
      const { data } = await axios.get(`${API}/tareas?${params.toString()}`, opts);
      setTareas(Array.isArray(data) ? data : []);
    } catch (err) { toast.error(err.response?.data?.detail || "Error al cargar las tareas"); }
    finally { setLoading(false); }
  }, [modulo, entidadTipo, entidadId, rolAsignado]);

  useEffect(() => { cargar(); }, [cargar]);

  const cambiar = async (tarea, estado) => {
    try {
      await axios.post(`${API}/tareas/${tarea._id}/estado`, { estado }, opts);
      cargar();
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudo cambiar el estado"); }
  };
  const borrar = async (tarea) => {
    try {
      await axios.delete(`${API}/tareas/${tarea._id}`, opts);
      toast.success("Tarea borrada");
      cargar();
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudo borrar"); }
  };
  const comentar = async (tarea, texto) => {
    if (!texto.trim()) return;
    try {
      await axios.post(`${API}/tareas/${tarea._id}/comentario`, { texto }, opts);
      cargar();
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudo comentar"); }
  };

  // Fix CU (2026-09-14): personas y roles que aparecen en las tareas cargadas.
  const personas = [...new Map(tareas.filter(t => t.asignado_user_id && t.asignado_nombre)
    .map(t => [String(t.asignado_user_id), t.asignado_nombre])).entries()]
    .sort((a, b) => a[1].localeCompare(b[1]));
  const roles = [...new Set(tareas.map(t => t.asignado_rol).filter(Boolean))].sort();
  const haySinAsignar = tareas.some(t => !t.asignado_user_id && !t.asignado_rol);

  const coincideAsignado = (t) => {
    if (!filtroAsignado) return true;
    if (filtroAsignado === "sin_asignar") return !t.asignado_user_id && !t.asignado_rol;
    const [tipo, valor] = filtroAsignado.split(/:(.*)/s);
    return tipo === "persona" ? String(t.asignado_user_id || "") === valor : (t.asignado_rol || "") === valor;
  };

  const visibles = tareas.filter(t =>
    (filtro === "todas" || (filtro === "sistema" ? t.origen === "sistema" : t.es_mia && t.estado !== "hecha"))
    && coincideAsignado(t));
  const pendientes = visibles.filter(t => t.estado !== "hecha");
  const frenadas = pendientes.filter(t => t.cuello_botella).length;

  return (
    <div className="bg-white rounded-2xl border border-gray-200/60 p-4" data-testid="tareas-panel">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div>
          <p className="text-sm font-bold text-gray-800">{titulo}</p>
          <p className="text-[11px] text-gray-400">
            {pendientes.length} pendiente(s){frenadas ? ` · ${frenadas} frenada(s) más de 48 h` : ""}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {/* Fix CU (2026-09-14): persona asignada o rol. */}
          <select value={filtroAsignado} onChange={e => setFiltroAsignado(e.target.value)}
            className="border border-gray-200 rounded-lg px-2 py-1 text-[11px] font-semibold bg-white text-gray-600"
            data-testid="tareas-filtro-asignado">
            <option value="">Cualquier persona o rol</option>
            {personas.length > 0 && (
              <optgroup label="Personas">
                {personas.map(([id, nombre]) => <option key={id} value={`persona:${id}`}>{nombre}</option>)}
              </optgroup>
            )}
            {roles.length > 0 && (
              <optgroup label="Roles">
                {roles.map(r => <option key={r} value={`rol:${r}`}>{r}</option>)}
              </optgroup>
            )}
            {haySinAsignar && <option value="sin_asignar">Sin asignar</option>}
          </select>
          {filtroAsignado && (
            <button onClick={() => setFiltroAsignado("")}
              className="px-2 py-1 rounded-lg text-[11px] font-semibold text-gray-500 hover:bg-gray-100 underline"
              data-testid="tareas-limpiar-asignado">Quitar</button>
          )}
          {[["todas", "Ver todas"], ["sistema", "Solo del sistema"], ["mias", "Mis pendientes"]].map(([v, l]) => (
            <button key={v} onClick={() => setFiltro(v)}
              className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold ${filtro === v ? "bg-[#2E6DA4] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
              data-testid={`tareas-filtro-${v}`}>{l}</button>
          ))}
          <button onClick={() => setVista(v => (v === "pendientes" ? "timeline" : "pendientes"))}
            className="px-2.5 py-1 rounded-lg text-[11px] font-semibold bg-gray-100 text-gray-600 hover:bg-gray-200" data-testid="tareas-vista">
            {vista === "pendientes" ? "Línea de tiempo" : "Pendientes"}
          </button>
          <button onClick={() => setNueva(true)} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-semibold"
            data-testid="tareas-nueva-btn">
            <Plus size={12} /> Nueva Tarea
          </button>
        </div>
      </div>

      {loading ? <div className="py-8 flex justify-center"><Loader2 className="animate-spin text-gray-400" /></div>
        : vista === "pendientes" ? (
          <div className="grid md:grid-cols-3 gap-3" data-testid="tareas-kanban">
            {ESTADOS.map(([estado, label]) => {
              const col = visibles.filter(t => t.estado === estado);
              return (
                <div key={estado} className="bg-gray-50/70 rounded-xl p-2" data-testid={`tareas-col-${estado}`}>
                  <p className="text-[11px] font-bold text-gray-500 uppercase px-1 pb-2 flex items-center gap-1">
                    {estado === "pendiente" ? <Clock size={11} /> : estado === "en_proceso" ? <Loader2 size={11} /> : <CheckCircle2 size={11} />}
                    {label} ({col.length})
                  </p>
                  <div className="space-y-2">
                    {col.length === 0 ? <p className="text-[11px] text-gray-400 px-1 pb-2">Nada por acá</p>
                      : col.map(t => <TareaCard key={t._id} tarea={t} onCambiar={cambiar} onBorrar={borrar} onComentar={comentar} onEditar={setEditando} />)}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="space-y-2" data-testid="tareas-timeline">
            {visibles.length === 0 ? <p className="text-sm text-gray-400 text-center py-6">Todavía no hay tareas en este módulo</p>
              : [...visibles].sort((a, b) => String(a.created_at).localeCompare(String(b.created_at))).map(t => (
                <div key={t._id} className="flex items-start gap-2" data-testid={`timeline-${t._id}`}>
                  <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${t.estado === "hecha" ? "bg-emerald-500" : t.cuello_botella ? "bg-red-500" : "bg-[#2E6DA4]"}`} />
                  <div className="flex-1 min-w-0 border-b border-gray-50 pb-2">
                    <p className="text-xs text-gray-700">
                      <span className={`font-bold ${t.origen === "sistema" ? "text-[#2E6DA4]" : "text-emerald-600"}`}>
                        ({t.origen === "sistema" ? "Sistema" : "Manual"})
                      </span>{" "}
                      {t.titulo}
                      {t.estado === "hecha" ? <span className="text-emerald-600"> — hecha por {t.completada_por || "—"}</span>
                        : <span className="text-amber-600"> — {t.estado === "en_proceso" ? "en proceso" : "pendiente"}</span>}
                    </p>
                    <p className="text-[10px] text-gray-400">
                      {formatDate(t.created_at)} · {t.entidad_label || "—"} · {t.asignado_nombre || t.asignado_rol || "sin asignar"}
                    </p>
                  </div>
                </div>
              ))}
          </div>
        )}

      {nueva && (
        <NuevaTareaModal
          contexto={{ modulo, entidad_tipo: entidadTipo, entidad_id: entidadId, entidad_label: entidadLabel }}
          onClose={() => setNueva(false)} onSaved={cargar} />
      )}
      {/* Fix CR (2026-09-14): el mismo modal, con la tarea cargada, para corregirla. */}
      {editando && (
        <NuevaTareaModal
          tarea={editando}
          contexto={{ modulo, entidad_tipo: entidadTipo, entidad_id: entidadId, entidad_label: entidadLabel }}
          onClose={() => setEditando(null)} onSaved={cargar} />
      )}
    </div>
  );
}
